import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import {
    IonPage,
    IonContent,
    IonCard,
    IonCardContent,
    IonButton,
    IonLabel,
    IonItem,
    IonRadioGroup,
    IonRadio,
    IonList,
    IonAlert,
    IonModal,
} from "@ionic/react";

interface QuestionData {
    singular: string;
    options: string[];
    correctAnswer: string;
}


const OneMany: React.FC = () => {

    const [currentIndex, setCurrentIndex] = useState(0);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [showImage, setShowImage] = useState(false);
    const [questions, setQuestions] = useState<QuestionData[]>([]);
    const modal = useRef<HTMLIonModalElement>(null);

    const shuffleArray = (array: any[]) => {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    };

    const handleSubmit = (option: React.SetStateAction<string | null>) => {
        setSelectedOption(option);
        const currentQuestion = questions[currentIndex];
        if (option === currentQuestion.correctAnswer) {
            // Automatically move to the next question
            setShowImage(true);
            setTimeout(() => {
                const nextIndex = currentIndex + 1;
                if (nextIndex < questions.length) {
                    setCurrentIndex(nextIndex);
                    setShowImage(false);
                    setSelectedOption(null);
                }
                else {
                    setCurrentIndex(0);
                    fetchQuestions();
                }
            }, 5000); // Delay reduced to 3 seconds
        }
    };

    const fetchQuestions = async () => {
        try {
            let data: QuestionData[] = [];

            if (process.env.REACT_APP_DEVELOPMENT !== 'WEB') {
                const response = await fetch(process.env.REACT_APP_API_URL + 'SingularPlural.json');
                data = await response.json();
            } else {
                const module = await import('../../../datas/SingularPlural.json');
                data = module.default;
            }

            const shuffledQuestions = shuffleArray(data);
            setQuestions(shuffledQuestions);
        } catch (error) {
            console.error('Error loading data:', error);
        }
    };

    useEffect(() => {
        fetchQuestions();
        document.title = "One and Many – LKG English Worksheet | Mama’s Guidance"; // Set page title
    }, []);

    return (
        <>
            <Helmet>
                <meta name="description" content='Help LKG children understand the concept of singular and plural with engaging “One and Many” activities on Mama’s Guidance—interactive learning through fun examples and visuals.' />
            </Helmet>
            {showImage === false ? (
                <>
                    <div className="contentPart showCenter fillPreposition additionWord">
                        <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                        <IonModal
                            ref={modal}
                            trigger="open-modal"
                            initialBreakpoint={1}
                            breakpoints={[0, 0.5, 0.75, 1]}
                            handleBehavior="cycle"
                            backdropDismiss={false}  // ✅ Prevents click-to-close
                            className="full-page-modal"
                        >
                            <IonContent className="ion-padding">
                                <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                <h1>One and Many</h1>

                                <p>
                                    Teach your little one the difference between <strong>one and many</strong> with this fun and interactive worksheet! This foundational LKG-level grammar activity introduces children to the concept of <strong>singular and plural</strong> using colorful images and easy examples.
                                </p>

                                <h2>🍎 What’s in the Worksheet?</h2>
                                <p>
                                    The worksheet presents images like <b><em>1 apple</em></b> and <b><em>3 apples</em></b>. Children are asked to <strong>circle, tick, or match</strong> items based on whether they show “one” or “many.” Some sheets may also include words ending in “s” to visually introduce plurals.
                                </p>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Identify <strong>singular (one) and plural (many</strong>) forms</li>
                                    <li>Strengthen <strong>observation and categorization skills</strong></li>
                                    <li>Build <strong>vocabulary</strong> with common everyday nouns</li>
                                    <li>Develop early grammar awareness through visual cues</li>
                                </ul>

                                <h2>👩‍🏫 How to Use the Worksheet</h2>
                                <ul>
                                    <li>Ask your child to name what they see in each picture.</li>
                                    <li>Discuss if it shows one object or more than one.</li>
                                    <li>Help them mark <strong>“One” or “Many”</strong> based on the picture.</li>
                                    <li>Say the plural aloud and emphasize the sound of <strong>“s” or “es.”</strong></li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/lkg/english-language/this-that-these-those"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>This–That & These–Those</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/english-language/rhyming-words"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Rhyming Words</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/english-language/opposites"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Opposites</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🧠 Why This Activity Matters</h2>
                                <p>
                                    Children naturally observe the world around them. This worksheet turns that observation into a learning experience by helping them <strong>understand quantity in language</strong>. It’s the perfect introduction to plurals!
                                </p>

                                <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                            </IonContent>
                        </IonModal>
                        <article className="visually-hidden">
                            <h1>One and Many</h1>

                            <p>
                                Teach your little one the difference between <strong>one and many</strong> with this fun and interactive worksheet! This foundational LKG-level grammar activity introduces children to the concept of <strong>singular and plural</strong> using colorful images and easy examples.
                            </p>

                            <h2>🍎 What’s in the Worksheet?</h2>
                            <p>
                                The worksheet presents images like <b><em>1 apple</em></b> and <b><em>3 apples</em></b>. Children are asked to <strong>circle, tick, or match</strong> items based on whether they show “one” or “many.” Some sheets may also include words ending in “s” to visually introduce plurals.
                            </p>

                            <h2>🎯 Learning Objectives</h2>
                            <ul>
                                <li>Identify <strong>singular (one) and plural (many</strong>) forms</li>
                                <li>Strengthen <strong>observation and categorization skills</strong></li>
                                <li>Build <strong>vocabulary</strong> with common everyday nouns</li>
                                <li>Develop early grammar awareness through visual cues</li>
                            </ul>

                            <h2>👩‍🏫 How to Use the Worksheet</h2>
                            <ul>
                                <li>Ask your child to name what they see in each picture.</li>
                                <li>Discuss if it shows one object or more than one.</li>
                                <li>Help them mark <strong>“One” or “Many”</strong> based on the picture.</li>
                                <li>Say the plural aloud and emphasize the sound of <strong>“s” or “es.”</strong></li>
                            </ul>


                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/lkg/english-language/this-that-these-those"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>This–That & These–Those</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/lkg/english-language/rhyming-words"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Rhyming Words</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/lkg/english-language/opposites"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Opposites</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🧠 Why This Activity Matters</h2>
                            <p>
                                Children naturally observe the world around them. This worksheet turns that observation into a learning experience by helping them <strong>understand quantity in language</strong>. It’s the perfect introduction to plurals!
                            </p>
                        </article>
                        <p>
                            <strong>Find the word that is plural of:</strong>{" "}
                            <h3>{questions[currentIndex]?.singular}</h3>
                        </p>

                        <IonRadioGroup
                            value={selectedOption}
                            onIonChange={(e) => handleSubmit(e.detail.value!)}
                        >
                            <IonList className="grid-item-container">
                                {questions[currentIndex]?.options.map((option, index) => (
                                    <IonItem
                                        className={`grid-item ${selectedOption === option
                                            ? option === questions[currentIndex]?.correctAnswer
                                                ? "correct"
                                                : "errorItems"
                                            : ""
                                            }`}
                                        key={index}>
                                        <IonLabel>{option}</IonLabel>
                                        <IonRadio slot="start" value={option} />
                                    </IonItem>
                                ))}
                            </IonList>
                        </IonRadioGroup>

                    </div>
                </>
            ) : (
                <>
                    <div className="contentPart showMiddle">
                        <img
                            style={{ height: "150px" }}
                            className="img-fluid"
                            src={process.env.REACT_APP_WELDONE_LINK}
                        />
                    </div>
                </>
            )}
        </>
    );
};

export default OneMany;
